import React, { useState, useEffect } from 'react';
import SavedCards from '../components/SavedCards';
import { getPaymentMethods } from '../services/paymentService';
import { useAuth } from '../context/AuthContext';
import './PaymentsDashboard.css';

/**
 * PaymentsDashboard Component
 * 
 * Comprehensive dashboard for managing payment methods and viewing
 * transaction history.
 */
const PaymentsDashboard = () => {
  useAuth();
  const [activeTab, setActiveTab] = useState('cards'); // 'cards', 'transactions', 'settings'
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch payment methods (for now, we'll use this as a placeholder for transactions)
  const fetchTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // For now, we'll show payment methods as transactions since we don't have a separate transaction endpoint
      const methods = await getPaymentMethods();
      
      // Ensure methods is an array
      if (!Array.isArray(methods)) {
        console.error('Expected array of payment methods, got:', methods);
        setError('Invalid response from server. Please try again.');
        return;
      }
      
      // Transform payment methods into transaction-like objects for display
      const mockTransactions = methods.map((method, index) => ({
        id: method.id,
        item_name: method.card_name || `${method.brand} Card`,
        amount: 0, // No amount for payment methods
        status: method.status,
        created_at: method.created_at,
        item_description: `Payment method created on ${new Date(method.created_at).toLocaleDateString()}`,
        payment_id: method.external_token ? method.external_token.substring(0, 8) + '...' : 'N/A'
      }));
      
      setTransactions(mockTransactions);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      setError('Failed to load payment methods. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'transactions') {
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Format transaction amount
  const formatAmount = (amount) => {
    return `R${(amount / 100).toFixed(2)}`;
  };

  // Format transaction date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get transaction status badge
  const getTransactionStatus = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'success':
        return <span className="status-badge success">Completed</span>;
      case 'pending':
        return <span className="status-badge pending">Pending</span>;
      case 'failed':
      case 'error':
        return <span className="status-badge failed">Failed</span>;
      default:
        return <span className="status-badge unknown">{status || 'Unknown'}</span>;
    }
  };

  return (
    <div className="payments-dashboard">
      <div className="dashboard-header">
        <h1>Payments Dashboard</h1>
        <p>Manage your payment methods and view transaction history</p>
      </div>

      {/* Payment System Status */}
      <div className="connection-status">
        <div className="status-indicator">
          <span className="status-dot connected"></span>
          <span className="status-text">
            Generic Payment System Active
          </span>
        </div>
        <div className="system-info">
          <span>Supports multiple payment providers</span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="dashboard-tabs">
        <button 
          className={`tab-button ${activeTab === 'cards' ? 'active' : ''}`}
          onClick={() => setActiveTab('cards')}
        >
          <span className="tab-icon">💳</span>
          Saved Cards
        </button>
        <button 
          className={`tab-button ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          <span className="tab-icon">📊</span>
          Transaction History
        </button>
        <button 
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <span className="tab-icon">⚙️</span>
          Payment Settings
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Saved Cards Tab */}
        {activeTab === 'cards' && (
          <div className="cards-tab">
            <SavedCards />
          </div>
        )}

        {/* Transaction History Tab */}
        {activeTab === 'transactions' && (
          <div className="transactions-tab">
            <div className="transactions-header">
              <h3>Transaction History</h3>
              <button 
                className="refresh-button"
                onClick={fetchTransactions}
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>

            {error && (
              <div className="error-message">
                <div className="error-icon">⚠️</div>
                <p>{error}</p>
                <button 
                  className="retry-button"
                  onClick={fetchTransactions}
                >
                  Try Again
                </button>
              </div>
            )}

            {loading && (
              <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Loading transactions...</p>
              </div>
            )}

            {!loading && !error && transactions.length === 0 && (
              <div className="no-transactions">
                <div className="no-transactions-icon">📊</div>
                <h4>No payment methods yet</h4>
                <p>Your payment methods will appear here once you save your first card.</p>
              </div>
            )}

            {!loading && !error && transactions.length > 0 && (
              <div className="transactions-list">
                {transactions.map((transaction) => (
                  <div key={transaction.id} className="transaction-item">
                    <div className="transaction-header">
                      <div className="transaction-info">
                        <div className="transaction-title">
                          {transaction.item_name || 'Payment'}
                        </div>
                        <div className="transaction-date">
                          {formatDate(transaction.created_at)}
                        </div>
                      </div>
                      <div className="transaction-amount">
                        {transaction.amount > 0 ? formatAmount(transaction.amount) : 'Payment Method'}
                      </div>
                    </div>
                    
                    <div className="transaction-details">
                      <div className="transaction-status">
                        {getTransactionStatus(transaction.status)}
                      </div>
                      {transaction.payment_id && (
                        <div className="transaction-id">
                          ID: {transaction.payment_id}
                        </div>
                      )}
                      {transaction.item_description && (
                        <div className="transaction-description">
                          {transaction.item_description}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payment Settings Tab */}
        {activeTab === 'settings' && (
          <div className="settings-tab">
            <h3>Payment Settings</h3>
            
            <div className="settings-section">
              <h4>Default Payment Method</h4>
              <p>Set your preferred payment method for automatic payments.</p>
              <div className="setting-item">
                <label>
                  <input type="checkbox" defaultChecked />
                  Use saved payment method by default
                </label>
              </div>
            </div>

            <div className="settings-section">
              <h4>Payment Notifications</h4>
              <p>Choose how you want to be notified about payments.</p>
              <div className="setting-item">
                <label>
                  <input type="checkbox" defaultChecked />
                  Email notifications for successful payments
                </label>
              </div>
              <div className="setting-item">
                <label>
                  <input type="checkbox" defaultChecked />
                  Email notifications for failed payments
                </label>
              </div>
              <div className="setting-item">
                <label>
                  <input type="checkbox" />
                  SMS notifications for payments
                </label>
              </div>
            </div>

            <div className="settings-section">
              <h4>Security Settings</h4>
              <p>Manage your payment security preferences.</p>
              <div className="setting-item">
                <label>
                  <input type="checkbox" defaultChecked />
                  Require CVV for saved card payments
                </label>
              </div>
              <div className="setting-item">
                <label>
                  <input type="checkbox" defaultChecked />
                  Send payment confirmation emails
                </label>
              </div>
            </div>

            <div className="settings-actions">
              <button className="save-settings-button">
                Save Settings
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="actions-grid">
          <button className="action-button">
            <span className="action-icon">➕</span>
            <span className="action-text">Add New Card</span>
          </button>
          <button className="action-button">
            <span className="action-icon">📊</span>
            <span className="action-text">View Statements</span>
          </button>
          <button className="action-button">
            <span className="action-icon">🔒</span>
            <span className="action-text">Security Settings</span>
          </button>
          <button className="action-button">
            <span className="action-icon">❓</span>
            <span className="action-text">Payment Help</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentsDashboard; 